<template>
  <img
    v-if="!isLazy || isAboveTheFold"
    :src="imgSrc"
    :width="width"
    :height="height"
    :alt="alt"
    :class="imgClass"
    :loading="loadingStrategy"
    :fetchpriority="fetchPriority"
    @load="onImageLoaded"
    @error="onImageError"
  />
  <img
    v-else
    class="lazy-load"
    :data-src="imgSrc"
    :src="placeholderSrc"
    :width="width"
    :height="height"
    :alt="alt"
    :class="imgClass"
  />
</template>

<script setup lang="ts">
const props = defineProps({
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    default: ''
  },
  width: {
    type: [Number, String],
    default: null
  },
  height: {
    type: [Number, String],
    default: null
  },
  isLazy: {
    type: Boolean,
    default: true
  },
  isAboveTheFold: {
    type: Boolean,
    default: false
  },
  imgClass: {
    type: String,
    default: ''
  },
  quality: {
    type: Number,
    default: 80
  },
  format: {
    type: String,
    default: 'webp'
  },
  fetchPriority: {
    type: String,
    default: 'auto'
  }
});

// 计算图片URL，根据需要添加CDN参数
const imgSrc = computed(() => {
  // 检查是否是存储在S3上的图片
  if (props.src.includes('s3.styletpff.com') || props.src.includes('musics3.styletpff.com')) {
    // 已经有CDN处理，返回原始URL
    return props.src;
  }

  // 检查是否是相对路径
  if (props.src.startsWith('/')) {
    return props.src;
  }

  // 外部URL，返回原始URL
  return props.src;
});

// 为懒加载图片生成占位符
const placeholderSrc = computed(() => {
  // 使用极小的透明数据URI作为占位符
  return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';
});

// 确定加载策略
const loadingStrategy = computed(() => {
  if (props.isAboveTheFold) {
    return 'eager'; // 首屏内容积极加载
  }
  return 'lazy'; // 其他内容懒加载
});

// 图片加载完成事件
const onImageLoaded = (event: Event) => {
  const img = event.target as HTMLImageElement;
  if (img && img.naturalWidth > 0) {
    // 图片加载成功
    emit('loaded', { 
      width: img.naturalWidth, 
      height: img.naturalHeight,
      src: props.src
    });
  }
};

// 图片加载失败事件
const onImageError = () => {
  emit('error', { src: props.src });
};

// 定义组件事件
const emit = defineEmits(['loaded', 'error']);
</script>

<style scoped>
/* 懒加载占位样式 */
.lazy-load {
  filter: blur(10px);
  transition: filter 0.3s;
}

/* 图片加载后移除模糊 */
img:not(.lazy-load) {
  filter: none;
}
</style> 